/*
 * What a step IS.
 *
 * The loop owns the lifecycle, lease, fence, heartbeat, budget, ordering, recovery. It has never
 * owned the model call, and that boundary is why the lifecycle could be audited and pinned without a
 * provider key anywhere near it. This file is the other side: the agent.
 *
 * It needs exactly two capabilities, and both are injected for the same reason the transport is:
 *
 *   • `ChatFn`, given messages, get a reply. In the browser that is the key-inject edge
 *                        (`/api/llm/<provider>/`), which puts the provider key SERVER-side where it
 *                        belongs; on a server it is the same edge or a direct upstream call. The
 *                        agent never sees a credential either way.
 *   • `ConversationIO`: a run's memory IS its conversation. Reading and appending it is the only
 *                        state this touches, and it is the state that already exists.
 *
 * WHAT IS NOT HERE YET, said plainly rather than implied: tool dispatch. A step that concludes with
 * a typed deliverable is a complete subagent, analysis, research, a judgement, and that is what
 * this does. A step that WRITES to the world needs the two-phase discipline in the log
 * (`input-available` before dispatch, resolved after) and a tool registry, and it is the next layer,
 * not a detail of this one.
 */
import type { AgentRun } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import {
  unsourcedValues,
  unsourcedRemedy,
  misattributed,
  misattributedRemedy,
  citationsFor,
  uncitedSources,
  uncitedRemedy,
  type RecordedCall,
} from './provenance';
import type { StepFn, StepResult, StepContext } from './loop';
import { isTurnRunId } from './turnid';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  /** Pictures the person attached to this message, for a model that can look at them. Base64 of
   *  the bytes with their media type — the wire shape every vision provider takes. */
  images?: { mediaType: string; base64: string }[];
  /** Tool calls this assistant turn issued. Carried on the message because the model must see its
   *  OWN call alongside the result: a result with no call to attach it to is uninterpretable. */
  toolCalls?: ToolCall[];
  /** Which call this `tool` message answers. */
  toolCallId?: string;
}

/** One tool invocation, as the model produced it. `argumentsJson` is untrusted in the ordinary way,
 *  it may be malformed or name fields that do not exist, and the dispatcher answers with a sentence
 *  rather than an exception. */
export interface ToolCall {
  id: string;
  name: string;
  argumentsJson: string;
}

/** What running a tool produced. `message` is mandatory and always prose: the same rule the server
 *  dispatcher enforces, restated here because this is the type the loop actually holds. */
export interface ToolOutcome {
  ok: boolean;
  message: string;
  data?: unknown;
  /**
   * Binary artifacts the call produced — a chart, a rendered image, an export.
   *
   * The LOOP does nothing with them, deliberately: they never enter a prompt, because bytes are
   * not something a model can read and a base64'd PNG is a hundred kilobytes of context it pays
   * for every turn thereafter. They are carried here only so the STORE can archive them beside the
   * result, which is where a person looks at them. A store that does not is not losing anything
   * the model needed.
   */
  attachments?: { filename: string; mediaType: string; bytes: Uint8Array }[];
  /**
   * THE CALL IS ANSWERED LATER, NOT NOW.
   *
   * A tool that waits — for a moment, for an event on the plant — has no result to record when it
   * returns, and recording one would be a lie the next step reads as "the wait is over". So its
   * dispatch returns at once with this set, and the step records NOTHING for it: the call stays open
   * in the log, which is exactly how the store derives that the run is waiting, and whatever
   * satisfies the wait later writes the result into that same call. `message` still says what was
   * arranged, for the audit trail — it is never shown to the model as an outcome.
   */
  deferred?: boolean;
}

/**
 * THE REFUSAL A TOOL OUTSIDE THE CALLER'S SUBSET GETS — one sentence, both lanes.
 *
 * An expert declares the tools it may use, and both lanes check that at the door. They were checking
 * it with two copies of the same sentence, one written out in the worker's server lane and one in
 * the browser's — which is the shape every other bug here has had: a model meeting one wording on
 * one lane and a different one on the other learns that its reach depends on where it is running,
 * which is the opposite of what a declared subset means.
 *
 * It lives beside `ToolOutcome` because that is the type it returns, and because this package is the
 * one thing both lanes already depend on. Sharing the sentence is not a tidiness argument: it is the
 * only way the two can be guaranteed to say the same thing without somebody remembering to.
 *
 * Deliberately vague about WHICH reason applies. The door knows the tool was not offered; it does not
 * know whether that is the specialist's subset, a policy tier, or a tool that only runs in the app,
 * and naming the wrong one sends a model off correcting something that was never the problem.
 */
export function outsideSubset(name: string): ToolOutcome {
  return {
    ok: false,
    message:
      `"${name}" is not available here — it is outside this specialist's tools, gated by policy, or ` +
      `only runs in the app. Use only the tools you were offered.`,
  };
}

/** What a store archived beside a tool result, when it archived anything referenceable. */
export interface ToolResultRecord {
  files?: { sha256: string; mediaType: string; filename: string; size: number }[];
}

/** Run one tool call. Injected like `chat` and `io`: the loop does not know what a tool IS, only
 *  that something can run one and will always say what happened. */
/** Runs one tool.
 *
 *  `ctx` carries the run, the fence it is executing under, and the THREAD it belongs to. The first
 *  two because a tool that mutates the run tree must present them — see `StepContext.epoch`. The
 *  third because a tool that reaches a person (a question, a confirmation) is asked IN a thread,
 *  and the only thing that knows which is the run being stepped: a dispatcher that stamped the
 *  conversation it happened to have open put a sub-agent's question under whichever thread the
 *  reader was looking at. A dispatcher with no interest in any of them simply declares one
 *  parameter. */
export type ToolDispatch = (call: ToolCall, ctx: { runId: string; epoch: bigint; conversationId: string }) => Promise<ToolOutcome>;

export interface ChatUsage {
  inputTokens: number;
  outputTokens: number;
  /** ABSENT when the provider did not report a cost, never 0.
   *
   *  This was `costUsd: number` and the adapter wrote `Number(usage.cost ?? 0)`, which is precisely
   *  the trap the two-tier contract exists to prevent: it collapsed "not reported" into a real
   *  measurement. The coverage matrix made it visible: every model showed cost 0, and there is no
   *  way to tell from that whether OpenRouter reported nothing or a free model genuinely cost
   *  nothing. Both are true for different rows and the column could not distinguish them.
   *
   *  A free model reporting a real 0 is DIFFERENT from a provider that stayed silent, and a billing
   *  panel that shows $0.00 for both is wrong about exactly the case where money is moving. */
  costUsd?: number;
  /** Input tokens the provider served from ITS cache. Absent = the provider said nothing, which is
   *  a different fact from a measured miss, and this is the figure that tells us whether prompt
   *  caching is actually working, so collapsing the two would hide the answer. */
  cachedInputTokens?: number;
  /** Input tokens written INTO the cache (Anthropic-family). The cost of establishing a cache, as
   *  opposed to the saving from hitting one. */
  cacheWriteTokens?: number;
  /** Generated reasoning tokens, where the provider separates them. Billed as output.  */
  reasoningTokens?: number;
}

export interface ChatReply {
  text: string;
  /** Tools the model wants run before it can continue. Present INSTEAD of a conclusion: a turn that
   *  called a tool has not finished thinking. */
  toolCalls?: ToolCall[];
  usage?: ChatUsage;
  /** When the first byte/frame arrived, if the transport can say. Absent is absent, see
   *  `StepTelemetry`. */
  ttftMs?: number;
  /** What the model thought before answering, on a model that streams it. Absent is absent — a
   *  non-reasoning model has no thinking to disclose, which is not the same as having thought
   *  nothing. */
  reasoning?: string;
  /** How long that thinking took. Present exactly when `reasoning` is. */
  reasoningMs?: number;
}

/** How the context sent to the model was composed.
 *
 *  THIS IS TIER 1, we assemble the context, so we can attribute every part of it, on every
 *  provider, in both modalities. It is the panel that makes a runaway context legible while it is
 *  still cheap to do something about.
 *
 *  `tokens` is the provider's REAL prompt total split by each part's share of the characters we
 *  sent. The total is measured; the split is proportional and says so. Tokenizing client-side to
 *  get an exact split would mean shipping a tokenizer per provider and still being wrong whenever
 *  one changed. */
export interface ContextBreakdown {
  chars: { system: number; history: number; task: number; dangling: number; steers: number; continuation: number };
  /** Absent when the provider reported no prompt total, never zero. */
  tokens?: { system: number; history: number; task: number; dangling: number; steers: number; continuation: number };
  messageCount: number;
}

/**
 * What a step cost and how long it took.
 *
 * TWO TIERS, and the type enforces the difference. Everything non-optional here is measured on our
 * side of the wire, so it is present on every provider and both modalities. Everything optional is
 * the provider's to report, and **absent means absent**, never 0.
 *
 * That distinction is the whole contract. A fabricated 0 is indistinguishable from a measured one
 * downstream, so a panel rendering "cache 0%" because the provider said nothing lies hardest about
 * the providers that report least, exactly backwards. Optionality here is what lets the UI render
 * ", " instead of inventing a number.
 */
export interface StepTelemetry {
  /** Wall clock for the whole step, including our own assembly. */
  elapsedMs: number;
  /** Send → response, measured on OUR side of the wire. Always present, on every provider, because
   *  it needs nobody's cooperation, it is two timestamps around the call. This is the number a user
   *  actually feels, and a running per-conversation average over it is the honest "how fast is this
   *  chat" figure. Separated from `elapsedMs` so context assembly is visible apart from inference. */
  inferenceMs: number;
  /** Send → FIRST byte, which is true TTFT and needs the streaming call. Absent on a non-streamed
   *  turn, where `inferenceMs` is the same measurement taken at the only observable moment. */
  ttftMs?: number;
  context: ContextBreakdown;
}

/**
 * One block of standing instruction, and whether it is worth caching.
 *
 * Separate from `messages` because a system turn is not a turn: it does not vary with the exchange,
 * every provider treats it specially, and the ones that cache prompts key on it being a stable
 * PREFIX. Expressed as our own shape rather than the SDK's so this file stays free of a provider
 * vocabulary, `cache: true` is a request, and what it becomes on the wire is the adapter's problem.
 */
export interface SystemBlock {
  content: string;
  /** Ask the provider to cache from here. See `chat-tanstack.ts` for what this does and does not
   *  buy, notably that every provider has a minimum length below which it is a no-op. */
  cache?: boolean;
}

/** One model call. The ONLY capability the agent needs that the loop does not provide. */
export type ChatFn = (
  messages: ChatMessage[],
  opts: {
    model: string;
    effort: string;
    tools?: unknown[];
    systemPrompts?: SystemBlock[];
    /** Whether this model can look at attached pictures. False sends a note in their place, so the
     *  model says it cannot see them rather than the request failing on a content type. */
    vision?: boolean;
    /** Fires once per text chunk as the model produces it, before the call resolves.
     *
     *  The call has always been streamed on the wire — `drainToReply` reads the chunks and
     *  measures first-token from them. What was missing was any way OUT: the reply resolved
     *  with the finished text and a caller could only paint once, at the end. A UI would then
     *  report a sub-second TTFT while showing nothing for ten seconds, because both numbers
     *  were true of different things.
     *
     *  Optional, and the reply is unchanged whether it is passed or not, so a non-interactive
     *  caller (the agent loop, an eval, the worker) ignores it and pays nothing. */
    onDelta?: (delta: string) => void;
    /** The same, for REASONING chunks on a thinking model. Its own hook rather than a flag on
     *  `onDelta`, because a UI puts the two in different places: the answer in the bubble, the
     *  thinking behind a disclosure above it. */
    onReasoning?: (delta: string) => void;
    /** A tool call the moment the model names it, ahead of its arguments and of the reply itself,
     *  so a UI can put the row up while the call is still being composed instead of landing five at
     *  once when the whole model call returns. The name is already mapped back to ours. */
    onToolCall?: (call: { id: string; name: string }) => void;
    /** Each fragment of a call's arguments as it lands, for the tools whose argument is the thing
     *  being made — an SCXML document written in front of the reader rather than delivered. */
    onToolArgs?: (callId: string, delta: string) => void;
    /** Extra provider-shaped request fields, merged over the reasoning request — an OpenRouter
     *  `provider` routing block, say. The caller owns what it asks for and pays for it. */
    modelOptions?: Record<string, unknown>;
    /** The caller's abort. Aborting it abandons the upstream request, so a caller that has stopped
     *  waiting also stops the meter — a deadline that only stopped the wait would keep billing. */
    signal?: AbortSignal;
  },
) => Promise<ChatReply>;

/** A run's memory. Reading and appending the conversation it owns, no other state. */
export interface ConversationIO {
  history(conversationId: string): Promise<ChatMessage[]>;
  /** May return the archive seq of the row written. The loop ignores it — an agent step has no use
   *  for a turn address — but a store that HAS one must be free to hand it to a caller that does
   *  (the chat UI, for fork and pin), and a `void` return here would forbid that everywhere. */
  append(conversationId: string, role: 'user' | 'assistant', text: string, reasoning?: string, reasoningMs?: number): Promise<number | void>;
  /** PHASE 1: the calls were issued and their outcomes are not yet known.
   *
   *  Written BEFORE dispatch, deliberately. A driver that dies mid-tool leaves a record saying "this
   *  was attempted", which the next step reads as UNKNOWN rather than failed, and unknown is the
   *  only honest reading, because on this platform a repeated call can mean writing to plant twice.
   *  Recording after dispatch would make a crashed write indistinguishable from one that never
   *  happened. */
  /** `reasoning` rides the phase-1 row when the step wrote NO prose — which is every step that calls
   *  a tool. Without it the thinking behind a tool call existed only while it streamed and was gone
   *  the moment the row landed; a thread re-read from the archive showed an agent that never thought. */
  appendToolCalls?(conversationId: string, calls: ToolCall[], reasoning?: string, reasoningMs?: number): Promise<void>;
  /** PHASE 2: the outcome, which resolves the record written above. */
  /**
   * Records the outcome — and MAY hand back what it archived alongside it.
   *
   * The return is `void` for every store that has nothing to say. A store that parks the call's
   * artifacts answers with their references, because the sha does not exist until the upload does:
   * a caller that wanted to show the picture it just produced had nothing to show, and rendering it
   * only after a reload makes a conversation look different depending on when you open it.
   */
  appendToolResult?(conversationId: string, callId: string, outcome: ToolOutcome): Promise<void | ToolResultRecord>;
}

/** Matches the store's cap. Enforced HERE too, because a headline over it is refused by the progress
 *  write, and a refused write reads to the driver as "this run went terminal underneath me", so a
 *  chatty model would silently look like a cancelled run. */
const HEADLINE_MAX_BYTES = 64;

const ELLIPSIS = '…';

function clampHeadline(s: string): string {
  const line = s.replace(/\s+/g, ' ').trim();
  const enc = new TextEncoder();
  if (enc.encode(line).length <= HEADLINE_MAX_BYTES) return line;
  // The budget is what is left AFTER the ellipsis, and the ellipsis is three bytes in UTF-8, not
  // one. Trimming to `MAX - 1` and then appending it emitted up to MAX + 2, over the store's cap,
  // so the progress write was refused, and a refused progress write reads to the loop as "this run
  // went terminal underneath me". A verbose model would have silently looked like a cancelled run.
  const budget = HEADLINE_MAX_BYTES - enc.encode(ELLIPSIS).length;
  let out = line;
  while (enc.encode(out).length > budget && out.length) out = out.slice(0, -1);
  return `${out}${ELLIPSIS}`;
}

/** The instruction the agent answers against. Built from the TYPED task rather than from prose, so a
 *  parent's `deliverable_spec` is what the child is actually held to. */
/*
 * THE STANDING INSTRUCTIONS, IN TWO BLOCKS, invariant first, then this run's.
 *
 * The split is for PREFIX STABILITY, which is the whole mechanism behind prompt caching on every
 * provider that offers it: a cache hit requires that the request begin with bytes the provider has
 * seen before. This text used to interleave the two, framing, then the run's objective, then the
 * reply format, so the first per-run character appeared a line in, and everything after it differed
 * on every run. Nothing could be shared even though most of it is identical fleet-wide.
 *
 * Ordered outermost-stable to innermost-volatile: identical for every agent on the platform, then
 * identical for every step of one run, then the turns. Reads the same to the model, instructions,
 * then the job, and is the layout the caches were designed for.
 */

/** Identical for every run, on every gateway, forever. The one block worth caching fleet-wide. */
const INVARIANT_INSTRUCTIONS = [
  'You are one agent in a tree of agents. Do the work you were given and nothing else.',
  '',
  'Reply with ONE JSON object and no other text:',
  '  {"headline": "<=7 words, what you are doing right now", "done": false}',
  ', when you still have work to do. You will be called again with your reply in the history.',
  '  {"headline": "...", "done": true, "summary": "<prose for whoever asked; Markdown is fine>", "deliverable": {...}}',
  ', when you have the answer. `deliverable` carries the declared fields.',
  '',
  'Say done only when the answer is complete. A truncated run is recoverable; a confident wrong one is not.',
].join('\n');

/** This run's job. Stable across its steps, which is the second thing worth caching, because a run
 *  makes many calls and re-sends this every time. */
/** How a terminal reason reads to the agent that has to recover from it. Plain sentences, because
 *  the recovery differs per reason and an enum name tells a model nothing about what to do. */
const ENDING: Record<number, string> = {
  2: 'it ran out of the steps it was allowed and never reached a conclusion',
  3: 'it reached its spending limit',
  5: 'the agent that asked for it abandoned that branch',
  6: 'the person watching stopped it',
  7: 'it failed with an error',
  8: 'the account had no credit left',
  9: 'the model returned nothing usable, repeatedly',
};

function runInstructions(run: AgentRun): string {
  const t = run.task;
  const lines = [`OBJECTIVE: ${t?.objective || '(none given)'}`];
  /*
   * WHAT THE ANSWER MUST CONTAIN, NOT FIELD NAMES. The spawn description has the parent write this
   * as a sentence ("Summarize the program's behaviour… Note any limitations…"), and it was presented
   * as "exactly these fields", so a PLC analyst on 2026-10-28 returned a deliverable whose keys were
   * those sentences. A spec that does list fields still reads correctly this way.
   */
  if (t?.deliverableSpec) lines.push(`DELIVERABLE: what your answer must contain — ${t.deliverableSpec}`);
  if (t?.inputJson) lines.push(`INPUT: ${t.inputJson}`);
  /*
   * A CONTINUATION IS TOLD HOW THE LAST ATTEMPT ENDED.
   *
   * Without this a resumed agent reads its own transcript, sees work that simply stops, and carries
   * on as though nothing went wrong — so it repeats what did not work and stops the same way, while
   * the person who asked it to try again has already read the reason on their screen. Naming the
   * ending, and saying plainly that repeating it is not the move, is the difference between "try
   * again" meaning something and meaning another identical failure.
   */
  const prior = run.priorEnding;
  if (prior) {
    const why = ENDING[prior.terminalReason] ?? 'it ended before finishing';
    const said = prior.result?.summary ? ` It reported: ${prior.result.summary}` : '';
    lines.push(
      `EARLIER ATTEMPT: a previous attempt at this same objective did not finish — ${why}.${said} ` +
        'The transcript above is that attempt. Build on what it established; do not start over, and do not repeat the ' +
        'approach that did not work. If the objective cannot be met, say so plainly rather than stopping the same way again.',
    );
  }
  return lines.join('\n');
}

/**
 * The standing instructions as blocks, most-stable first.
 *
 * `extra` is where a CALLER'S OWN standing text goes — the machine lane's expert prompt, above all.
 * An expert IS its prompt: the roster row carries a base prompt and an org's addition to it, and a
 * lane that dropped them would be running a different expert wearing the same name.
 *
 * Placed AFTER the fleet invariants and BEFORE the run's own job, which is the order stability runs
 * in: the invariants never change, an expert's prompt changes when someone edits the roster, and the
 * objective changes every run. Prompt caching reads a prefix, so anything less stable sitting
 * earlier would invalidate everything after it on each call.
 */
export function systemBlocks(run: AgentRun, extra: SystemBlock[] = []): SystemBlock[] {
  return [{ content: INVARIANT_INSTRUCTIONS, cache: true }, ...extra, { content: runInstructions(run), cache: true }];
}

/** The same instructions as one string. Kept because the context breakdown measures what was sent
 *  as a single `system` share, and because a caller driving `ChatFn` directly may not carry blocks. */
export function systemPrompt(run: AgentRun, extra: SystemBlock[] = []): string {
  return systemBlocks(run, extra)
    .map((b) => b.content)
    .join('\n\n');
}

/** Split the provider's REAL prompt total across the parts we sent, by character share.
 *
 *  Returns undefined when the provider reported no prompt total: the caller renders ", ", never 0,
 *  because a fabricated zero is indistinguishable from a measured one and would misrepresent
 *  exactly the providers that report least. */
function attribute(chars: ContextBreakdown['chars'], usage: ChatUsage | undefined): ContextBreakdown['tokens'] {
  const total = usage?.inputTokens ?? 0;
  if (!total) return undefined;
  const sum = Object.values(chars).reduce((a, b) => a + b, 0);
  if (!sum) return undefined;
  const share = (n: number) => Math.round((n / sum) * total);
  return {
    system: share(chars.system),
    history: share(chars.history),
    task: share(chars.task),
    dangling: share(chars.dangling),
    steers: share(chars.steers),
    continuation: share(chars.continuation),
  };
}

/** The task, phrased as the request it is. Kept separate from the system prompt because the system
 *  prompt is the ROLE (you are one agent in a tree; answer in this shape) and this is the ASK. */
export function taskAsRequest(run: AgentRun): string {
  const t = run.task;
  const parts = [t?.objective || 'Do the work described in your instructions.'];
  if (t?.inputJson) parts.push(`Input: ${t.inputJson}`);
  if (t?.deliverableSpec) parts.push(`Your answer must contain: ${t.deliverableSpec}`);
  return parts.join('\n\n');
}

/** What one step of an agent says: a label for the work, whether it is finished, the sentence for
 *  whoever asked, and the payload a machine acts on. */
export interface StepReply {
  headline?: string;
  done?: boolean;
  summary?: string;
  deliverable?: unknown;
}

/** What JSON.parse says about the object a reply tried to send — the parser's own message, with the
 *  position, so the model can find the fault. Empty when it parses (the fault was elsewhere). */
function jsonFault(text: string): string {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  const body = fenced?.[1] ?? text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1);
  if (!body) return 'no JSON object found';
  try {
    JSON.parse(body.trim());
    return '';
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}

/** Pull the model's JSON out of a reply. Models wrap it in prose, in fences, or emit it clean. */
function parseReply(text: string): StepReply | null {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  const candidates = [fenced?.[1], text, text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1)];
  for (const c of candidates) {
    if (!c) continue;
    /*
     * ONE BRACE TOO MANY is forgiven here too, as `parseStepReply` already forgives it. Only the
     * viewer had the rule, so the loop refused an envelope the transcript showed as fine: a PLC
     * analyst on 2026-10-28 closed one brace too many four times running, was told each time its
     * reply "could not be read", and rewrote the same correct answer until it happened to count right.
     */
    for (const body of [c.trim(), overclosed(c.trim())]) {
      try {
        const v: unknown = JSON.parse(body);
        if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, never>;
      } catch {
        /* try the next shape */
      }
    }
  }
  return null;
}

/** The four fields a step is composed of. Anything else in the object is not this shape. */
const STEP_FIELDS = new Set(['headline', 'done', 'summary', 'deliverable']);

/**
 * IS THIS TURN A STEP, rather than something an agent said?
 *
 * An agent's reply IS this envelope — the loop parses it, and the archive keeps the model's words
 * verbatim, which is right: an auditor needs to see exactly what was emitted. A READER handed it
 * raw gets a machine protocol rendered as prose, run through markdown, and markdown eats the
 * underscores: `a_CPU_Usage` came out as `aCPUUsage` in a live audit transcript, so the record
 * showed a key the deliverable does not contain.
 *
 * STRICT, AND DELIBERATELY LOSSY-AVERSE. It answers yes only for the exact four-field shape the
 * loop composes, with a `done` flag present. An object carrying anything else — a model that added
 * a field, an answer that happens to be JSON — is NOT this, and the caller keeps rendering it as it
 * always did. Dropping a key a reader needed would be worse than the mangling this fixes, so the
 * unusual case is left alone rather than partly understood.
 *
 * Here beside the parser the loop itself uses, so "what a step looks like" has one definition and a
 * viewer cannot drift from the thing it is viewing.
 */
export function parseStepReply(text: string): StepReply | null {
  /*
   * THE WHOLE TEXT, NOT A SHAPE DUG OUT OF IT — and this is the difference between the loop's
   * parser and this one.
   *
   * `parseReply` is deliberately lenient: it will slice from the first `{` to the last `}` to find
   * intent in messy model output, which is right when the job is "what did it decide". It is wrong
   * when the job is "what may I stop showing a person". A transcript surface folds an agent's
   * several messages into one turn, so that turn's text is the run's working narration AND its
   * concluding envelope — and a lenient parse pulls the envelope out of the middle, after which
   * rendering only the envelope's parts silently discards every line of narration around it.
   *
   * Observed exactly that way: "First read: CPU.Usage. / Second read: CPU.Load (1m). / Third read:
   * Memory & Storage RAM…" vanished from a live transcript, leaving only the summary.
   *
   * So: the text must BE the object (or a single fenced block containing nothing else). A turn that
   * merely contains one is not this, and keeps the rendering it always had.
   */
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)```$/);
  // No cheap "starts with { and ends with }" pre-check: `JSON.parse` already refuses anything that
  // is not exactly one JSON document, and the three shapes it would accept but we do not — a number,
  // an array, null — are refused right below. A guard that no mutation can break is dead code.
  const body = (fenced ? fenced[1] : trimmed).trim();
  let v: StepReply;
  try {
    const parsed: unknown = JSON.parse(overclosed(body));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    v = parsed as StepReply;
  } catch {
    return null;
  }
  if (typeof v.done !== 'boolean') return null;
  if (Object.keys(v).some((k) => !STEP_FIELDS.has(k))) return null;
  return v;
}

/**
 * ONE BRACE TOO MANY, forgiven.
 *
 * A model concluding a long envelope closes a brace it never opened more often than it does
 * anything else wrong — observed as a run that finished correctly and then printed its whole
 * envelope into the transcript as raw JSON, because the strict parser refused `…"}}` and the
 * renderer fell back to markdown. The document is unambiguous: every string is closed and the
 * only fault is trailing closers past the balance. Those are dropped, string-aware, and nothing
 * else is touched — an envelope that is wrong in any other way still fails, as it should.
 */
function overclosed(body: string): string {
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (const c of body) {
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{' || c === '[') depth += 1;
    else if (c === '}' || c === ']') depth -= 1;
  }
  if (inStr || depth >= 0) return body;
  let out = body;
  for (let extra = -depth; extra > 0 && out.endsWith('}'); extra -= 1) out = out.slice(0, -1).trimEnd();
  return out;
}

/**
 * NARRATION AND ENVELOPE, SEPARATED — because a step's turn is very often both.
 *
 * `parseStepReply` answers only for a text that IS the envelope, and that strictness exists for a
 * reason worth keeping: a lenient parse dug the object out of the middle of a turn and rendering
 * only its parts silently deleted every line of narration around it. But "not exactly the envelope"
 * was then treated as "not an envelope at all", and the fallback is to run the whole turn through
 * markdown — so an agent that said three sentences and then concluded printed its concluding JSON
 * into the transcript as prose. Observed live, in an agent's own thread, with markdown eating the
 * underscores as it went: `Sine_00` displayed as `Sine00`, in italics, on the audit surface.
 *
 * Both halves are answerable at once. The envelope is the LAST thing a step emits, so the only
 * candidate is a SUFFIX — the final top-level JSON object, ending at the final character. Take
 * that, hand it to the same strict parser, and keep everything before it verbatim. Nothing is dug
 * out of the middle, nothing is dropped, and neither failure can recur.
 *
 * Refuses in exactly the cases the strict parser refuses, and the caller then renders the text as
 * it always did.
 */
export function splitStepReply(text: string): { narration: string; reply: StepReply | null } {
  const trimmed = text.trimEnd();
  // Fenced first, because a model that narrates and then concludes usually fences the conclusion,
  // and that text ends in backticks rather than a brace — the bare-suffix scan below cannot see it.
  const fence = /```(?:json)?\s*([\s\S]*?)```$/.exec(trimmed);
  if (fence) {
    const reply = parseStepReply(fence[1].trim());
    if (reply) return { narration: trimmed.slice(0, fence.index).trim(), reply };
  }
  const start = lastTopLevelObject(trimmed);
  if (start < 0) {
    // No clean suffix — but the whole text may still BE the envelope (an over-closed one has no
    // balanced suffix to find). When it parses, it was all envelope and there is no narration.
    const whole = parseStepReply(text);
    return { narration: whole ? '' : text, reply: whole };
  }
  const reply = parseStepReply(trimmed.slice(start));
  if (!reply) return { narration: text, reply: null };
  return { narration: trimmed.slice(0, start).trim(), reply };
}

/**
 * Where the final top-level `{…}` begins, or -1 when the text does not END with one.
 *
 * String-aware, because a brace inside a quoted value is not structure — an envelope whose summary
 * quotes `{` would otherwise be cut at the wrong place and fail to parse, silently sending the
 * whole turn back to the raw rendering this exists to remove. Escapes are tracked for the same
 * reason: a `\"` inside a string does not end it.
 */
function lastTopLevelObject(text: string): number {
  const end = text.trimEnd().length;
  // A FAST PATH, not a correctness guard: `parseStepReply` refuses anything that is not exactly one
  // JSON document, so a text not ending in `}` could never produce a parsing suffix anyway. It is
  // here because most turns are prose and this saves scanning every one of them character by
  // character on every render. Do not write a test for it — there is no behaviour behind it.
  if (end === 0 || text[end - 1] !== '}') return -1;
  let depth = 0;
  let start = -1;
  let inStr = false;
  let esc = false;
  for (let i = 0; i < end; i += 1) {
    const c = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{' || c === '[') {
      if (depth === 0 && c === '{') start = i;
      depth += 1;
    } else if (c === '}' || c === ']') {
      // Allowed to go negative on unbalanced input — a stray `}` in prose leaves the depth below
      // zero at the end, which the return already reads as "no top-level object here".
      depth -= 1;
    }
  }
  return depth === 0 ? start : -1;
}

/** What a step is producing RIGHT NOW: the prose, the thinking, and the tool calls the model has
 *  named so far — the last with whatever arguments have arrived. Everything a viewer can paint
 *  before the step lands; nothing anything decides on. */
export interface LiveStep {
  text: string;
  reasoning: string;
  tools: { id: string; name: string; argumentsJson: string }[];
  /** The three above in the ORDER they happened. A step thinks, calls, thinks again, speaks; a
   *  viewer that draws buckets draws it wrong, so the sequence travels too. */
  parts: LivePart[];
}
export type LivePart = { kind: 'reasoning'; text: string } | { kind: 'text'; text: string } | { kind: 'tool'; id: string };

export interface AgentStepOptions {
  chat: ChatFn;
  io: ConversationIO;
  /** Fallback when the run names none. */
  defaultModel?: string;
  /** The catalog, in the provider's tool shape. A function because it is cheap and the caller may
   *  SCOPED PER RUN, which is what makes a specialist possible: an expert declares the subset of
   *  tools it may use, and a tree can hold runs with different experts, so "what may this agent
   *  call" is a property of the RUN and not of the tree that drives it. A caller that does not care
   *  ignores the argument. */
  tools?: (run: AgentRun) => unknown[];
  /** Runs one tool. Required for tools to be offered at all, see `toolsUsable`. */
  dispatch?: ToolDispatch;
  /** Standing text this caller adds to every step — see `systemBlocks`. A function because it is
   *  cheap and may depend on the run. */
  systemExtra?: (run: AgentRun) => SystemBlock[];
  /**
   * WHAT THE MODEL IS PRODUCING RIGHT NOW — the only thing here that fires DURING a model call.
   *
   * Everything else about a step is reported once it has landed, which is right for a record and
   * useless for a viewer: an agent that calls no tools makes ONE call, appends ONE message at the
   * end, and looks like a blank page for the whole time it is working.
   *
   * BOTH CHANNELS, and the second is the one that matters here. An agent step that calls a tool
   * emits NO PROSE AT ALL — checked against a real run's archive: every assistant row was a tool
   * call, and the only text in the whole thread was the final answer. So streaming the text alone
   * left exactly the gap it was added to close: twelve to twenty-five seconds per step with nothing
   * on screen, because during those seconds the model is THINKING, not writing.
   *
   * THROTTLED BY THE CALLER, not here — how often it is worth paying to publish depends entirely on
   * where it is being published to, and the step has no opinion about that.
   */
  onPartial?: (partial: LiveStep, run: AgentRun, ctx: StepContext) => void;
  /**
   * VALIDATE THE DELIVERABLE before the run is allowed to call itself done.
   *
   * A caller with a declared output contract — the machine lane's `outputSchema`, where a state
   * machine branches on the fields — cannot accept "the model said done". Returning a remedy turns
   * the reply into ordinary progress carrying the correction, so the model gets exactly one more
   * try inside the same step budget rather than the run concluding on output nobody could use.
   *
   * Absent means no contract, which is the narrative case: what a human reads.
   */
  validateDeliverable?: (deliverableJson: string, text: string) => { ok: true } | { ok: false; remedy: string };
}

/**
 * Tools are offered ONLY if their two phases can be recorded.
 *
 * A conversation that cannot write "this call was issued" and "this is what happened" would leave a
 * crashed tool call invisible, indistinguishable from one that never ran. On a platform where a
 * repeated call can write to plant twice, that is the difference between a recoverable step and a
 * physical duplicate. So an IO without both hooks gets no tools, rather than tools whose failures
 * cannot be represented.
 */
function toolsUsable(o: AgentStepOptions): boolean {
  return !!o.tools && !!o.dispatch && !!o.io.appendToolCalls && !!o.io.appendToolResult;
}

/**
 * Build the step the loop calls.
 *
 * UNPARSEABLE OUTPUT DOES NOT CONCLUDE A RUN. It reports progress and lets the run continue to its
 * step cap, which ends it with a reason a parent can interpret ("ran out of road") rather than a
 * conclusion drawn from output nobody could read. Same one-directional bias as the propagation gate:
 * over-running costs one more call, under-running ships a fabrication as a finding.
 */
/**
 * EVERYTHING THIS RUN ACTUALLY RECEIVED, as one string.
 *
 * Its task — objective, deliverable spec and inputs, which is what it was TOLD — plus every tool
 * result and every message handed to it: a child's return, a peer's note, the user's own words. The
 * agent's OWN turns are excluded, and that exclusion is the whole point: a figure is not sourced by
 * having been said by the agent that is now reporting it.
 *
 * Concatenated rather than structured because the only question asked of it is "does this appear",
 * and the answer must not depend on which slot it appeared in.
 */
function evidenceFor(history: readonly ChatMessage[], run: AgentRun): string {
  const task = [run.task?.objective, run.task?.deliverableSpec, run.task?.inputJson].filter(Boolean).join('\n');
  const received = history.filter((m) => (m.role === 'tool' || m.role === 'user') && !isOurs(m.content)).map((m) => m.content);
  return [task, ...received].join('\n');
}

/**
 * OUR OWN SCAFFOLDING IS NOT EVIDENCE, and leaving it in defeated the check completely.
 *
 * The provenance remedy NAMES the figures it is asking about, and it is delivered the only way a
 * driver can speak to a model mid-run: appended to the conversation in the user slot. So the value
 * became part of the history, the history is the evidence, and on the very next attempt the number
 * was sourced — by the act of having asked where it came from. Observed live: an agent challenged
 * over a computed 13.0 explained itself, concluded, and carried no finding at all.
 *
 * The same is true of every SYSTEM line the loop writes: the dangling-tool warning, the rejected-
 * answer notice. They are the platform talking to the agent, not the world reporting a measurement,
 * and nothing the platform says about a number can be what makes that number real.
 */
const isOurs = (content: string): boolean => content.startsWith('SYSTEM: ');

/** The provenance challenge, marked so the run's own log can answer "was this already put to it".
 *  A prefix rather than a flag: the log is the only per-run state that survives a change of driver. */
const ASKED_FOR_SOURCES = 'SYSTEM: before that answer stands — ';
/*
 * WHAT A CHALLENGED AGENT DOES NEXT, the same for all three checks.
 *
 * Told only "check which reading belongs to which", an agent answered the challenge instead of the
 * task: in 8 of 11 local retries the new answer was about the challenged number, in 3 the correct
 * answer was gone, and its last answer replaces the first. It also went back and read everything
 * again, and the repeated reads failed. So it is told to resend the whole answer, change only what
 * the note is about, use what it already has, and keep a value it knows is right.
 */
const RESEND_WHOLE_ANSWER =
  'Send your whole answer again: keep everything that was right and change only what this note is about. ' +
  'Do not read anything again; use what you already have. If the value is right, keep it and say which call it came from.';

/**
 * WHAT WAS ASKED, AND WHAT CAME BACK — paired, from the log.
 *
 * A call is recorded as an assistant turn carrying `toolCalls`; its answer arrives later as a `tool`
 * message naming that call's id. Rejoining them is what makes "we asked about X and got Y" a
 * question with an answer, and that is the whole basis for catching a real reading reported against
 * the wrong thing.
 *
 * A call with no answer is dropped rather than paired with nothing: an unanswered call tells us what
 * was asked and nothing about what is true, so it can neither confirm nor contradict a claim.
 */
function recordedCalls(history: readonly ChatMessage[]): RecordedCall[] {
  const asked = new Map<string, { name: string; argumentsJson: string }>();
  for (const m of history) for (const c of m.toolCalls ?? []) asked.set(c.id, { name: c.name, argumentsJson: c.argumentsJson });

  const out: RecordedCall[] = [];
  for (const m of history) {
    if (m.role !== 'tool' || !m.toolCallId) continue;
    const a = asked.get(m.toolCallId);
    if (a) out.push({ name: a.name, argumentsJson: a.argumentsJson, output: m.content });
  }
  return out;
}

/** A question or an answer is a sentence or two. Longer is a paste, and this line is carried in the
 *  parent's context for every turn after the return, so it is bounded rather than trusted. */
const MAX_ASKED_CHARS = 300;

/**
 * WHAT THIS RUN ASKED THE PERSON — `question → answer`, one per `ask_user`, in the order asked.
 *
 * From the call record, the same pairing the provenance checks use: what the person answered is the
 * tool's output, and an unanswered question's output is the tool's own sentence saying so, which is
 * what the parent should read in that case too.
 */
export function askedOf(calls: readonly RecordedCall[]): string[] {
  const clip = (t: string): string => (t.length > MAX_ASKED_CHARS ? `${t.slice(0, MAX_ASKED_CHARS)}…` : t);
  const out: string[] = [];
  for (const c of calls) {
    if (c.name !== 'ask_user') continue;
    let question = '';
    try {
      question = String((JSON.parse(c.argumentsJson) as { question?: unknown }).question ?? '').trim();
    } catch {
      continue; // arguments that do not parse never reached the person
    }
    if (question) out.push(`${clip(question)} → ${clip(c.output.trim())}`);
  }
  return out;
}

export function makeAgentStep(opts: AgentStepOptions): StepFn {
  const { chat, io, defaultModel = 'openrouter/auto', tools, dispatch, systemExtra, validateDeliverable, onPartial } = opts;
  const useTools = toolsUsable(opts);
  return async (run: AgentRun, ctx: StepContext): Promise<StepResult> => {
    const startedAt = Date.now();
    // Tally as we assemble. Doing it HERE rather than by re-walking `messages` afterwards is what
    // keeps the attribution honest: once the parts are concatenated into one array, "which of these
    // is a tool result and which is a steer" is a guess.
    const chars = { system: 0, history: 0, task: 0, dangling: 0, steers: 0, continuation: 0 };
    const messages: ChatMessage[] = [];
    const put = (role: ChatMessage['role'], content: string, bucket: keyof typeof chars) => {
      messages.push({ role, content });
      chars[bucket] += content.length;
    };
    /** History is replayed VERBATIM, because a tool message without its `toolCallId`, or an
     *  assistant turn without the `toolCalls` it issued, is uninterpretable to the model and
     *  rejected outright by most providers. Rebuilding it as `{role, content}` silently dropped
     *  both. */
    const replay = (m: ChatMessage) => {
      messages.push(m);
      chars.history += m.content.length;
    };

    const history = await io.history(run.conversationId);
    /*
     * THE STANDING INSTRUCTIONS TRAVEL BESIDE THE TURNS, NOT AS ONE.
     *
     * They used to be pushed as a `role: 'system'` message. That works: every provider accepts it,
     * but it forfeits the two things the dedicated channel buys: the adapter can place it where the
     * provider actually wants it (Gemini's `systemInstruction`, Anthropic's top-level `system`), and
     * it can carry a cache marker, which a message cannot.
     *
     * Still counted into the `system` share of the context breakdown, because the model is charged
     * for it either way and an accounting that stopped seeing it would understate every prompt.
     */
    const system = systemBlocks(run, systemExtra?.(run) ?? []);
    chars.system += system.reduce((n, b) => n + b.content.length, 0);
    // THE TASK, ALWAYS, and never written to the log.
    //
    // It used to be appended as a user message on the first step. That put it in the durable log,
    // where the driver's steer inbox, which reads user messages, handed it straight back to the
    // agent as something the USER had just said. A run's own framing became indistinguishable from
    // a live correction, so steer and interrupt semantics applied to it.
    //
    // It is derived from the run's task, which is already stored, so persisting it was also a second
    // home for one fact. Recomputing it every step is free and idempotent, and it keeps the log
    // holding only what was actually exchanged.
    put('user', taskAsRequest(run), 'task');
    for (const m of history) replay(m);

    // ONE INVARIANT: a turn ends with a user message. Everything below is that rule.
    //
    // It is not a formality. A fresh run that sends only a system message is a conversation with no
    // request in it, Gemini's OpenAI-compatible surface rejects it outright ("contents is not
    // specified", because a system message becomes systemInstruction and leaves the turn list
    // empty), and providers that accept it answer something arbitrary, which is worse. And a
    // CONTINUING run that appends assistant turn after assistant turn gives the model nothing new to
    // respond to: it restates its last reply, forever, until the step cap ends it. Both were
    // observed against a live model; neither is visible against a fake, which accepts any body.

    // A tool call whose outcome was never recorded is UNKNOWN, not failed. The agent is told so in
    // those words, because the recovery action for unknown is to go and look, not to retry, which
    // on this platform can mean writing to plant twice.
    for (const d of ctx.dangling) {
      put(
        'user',
        `SYSTEM: a previous attempt called \`${d.name}\` with ${d.inputJson} and its outcome was never recorded. ` +
          'Its effect is UNKNOWN, not failed. Check the current state before assuming either way; do not repeat it blindly.',
        'dangling',
      );
      /*
       * AND SETTLE IT, ONCE. The call stays open in the archive until something answers it, and the
       * tree reports an open call on every takeover — so the note above arrived at every step of an
       * adopted run, and a model told "check before you assume" at every step checked at every step:
       * observed, a designer that had finished its board re-reading the board and the model twenty
       * times over, never concluding, because each step opened with the same unresolved question.
       * Recorded as an outcome the model can read back as a tool message — "unknown" in words — so
       * the next step sees a closed call, and the next takeover finds nothing dangling.
       */
      await io.appendToolResult?.(run.conversationId, d.toolCallId, {
        ok: false,
        message: 'Outcome never recorded: the driver that made this call went away before what came back was written. Its effect is unknown — the state was checked in the step that followed.',
      });
    }
    // What the user said since the last step, in the order they said it — and what a PEER AGENT
    // said, which is the same delivery path and must not read as the same voice.
    //
    // Both arrive as user-role messages appended to this run's conversation, because that is the one
    // path a driver picks up mid-run. Left identical, a sibling's note would carry the operator's
    // authority: "the board is wrong, delete it" from a peer would be obeyed as if the account owner
    // had asked. The label is what separates a colleague's opinion from an instruction, and it is
    // taken from the DO's record of who wrote the row, never from the text.
    for (const s of ctx.steers) {
      /*
       * BY NAME, and the name is the point.
       *
       * This said "NOTE FROM AGENT run-3f2a8c…", which is a uuid. A run is never told its own id and
       * nothing tells it its siblings', so the recipient could not relate that string to any agent
       * it knew about — the label proved the words were not the user's, which is the safety half,
       * and said nothing about who was speaking, which is the half that makes a message worth acting
       * on. Every other verb in this family addresses an agent by name; a message arrives by name.
       *
       * The id stays as a fallback for a sender whose spawner named nothing, because "not the user"
       * is the load-bearing claim and must survive an unnamed sender.
       */
      if (!s.fromRunId) put('user', s.text, 'steers');
      /*
       * AND A COORDINATOR IS NOT A PEER.
       *
       * The thread that spawned this agent can message it too, and its id is a TURN — which has no
       * label to look up and no run row behind it, so the peer line rendered as "MESSAGE FROM AGENT
       * turn-b872e5e1-… (a peer, not the user)": a uuid the recipient has never seen, describing as
       * a sibling the very thread that gave it its objective. Both halves misinform. Naming it for
       * what it is costs one branch and tells the recipient something it can act on — this is the
       * agent that briefed you, so a correction from it supersedes the brief.
       *
       * Still not the user, and said so, because that is the load-bearing claim on this path.
       */
      else if (isTurnRunId(s.fromRunId))
        put('user', `MESSAGE FROM THE AGENT THAT STARTED YOU (not the user): ${s.text}`, 'steers');
      else put('user', `MESSAGE FROM AGENT ${s.fromName || s.fromRunId} (a peer, not the user): ${s.text}`, 'steers');
    }

    // Nothing above supplied a user turn, so this is a continuation with no new information, the
    // agent thought last time and now has to either use that thinking or finish. Naming the step
    // number matters: it is the only way the agent can tell how much road is left, and a run that
    // knows it is near its cap converges instead of being truncated mid-thought.
    // A TOOL RESULT ends a turn as validly as a user message does: the model has something new to
    // respond to, which is the whole point of the invariant. Appending "Continue." after a tool
    // result would tell the model nothing it does not already have and would push the result further
    // from the end of the prompt.
    /*
     * THE LAST STEP IS TOLD IT IS THE LAST STEP — unconditionally, and this is the whole point.
     *
     * The continuation below is written only when the previous turn was NOT a tool result, on the
     * sound reasoning that a tool result is already something to respond to. The consequence was
     * that an agent deep in a chain of tool calls — exactly the agent about to run out — was never
     * told how much road it had left. Observed live: an analyst said in prose that it was wrapping
     * up, then kept calling tools until the cap took it, and its parent received "ran out of turns"
     * instead of the answer it was three sentences from writing.
     *
     * It also stops offering tools. A tool call issued on the final step produces a result nobody
     * will be called back to read, so the offer is an invitation to spend the last step on nothing —
     * and a model that can see a tool will reasonably reach for one when it has been asked a
     * question it cannot fully answer.
     */
    if (ctx.lastCall) {
      put(
        'user',
        'This is your LAST step — there will be no further calls, and no tool you request now will ' +
          'come back to you. Reply with done true and report what you actually have: your findings ' +
          'so far, what you were in the middle of, and what is still unknown. A partial answer that ' +
          'says which part is missing is worth far more than no answer.',
        'continuation',
      );
    }
    const last = messages[messages.length - 1].role;
    if (!ctx.lastCall && last !== 'user' && last !== 'tool') {
      const left = Math.max(0, (run.task?.constraints?.stepCap || 0) - run.stepCount);
      /*
       * A REPLY THAT SAID `"done"` AND STILL REACHED HERE tried to return and could not be read.
       * "Do not repeat your previous reply" is then the one instruction that cannot help: the
       * author has delivered, is told not to say it again, and spends its remaining steps caught
       * between the two. Observed live — a widget author's envelope broke at character 12,081 (a
       * brace closed early inside the widget's config), and it went from step 17 to its cap of 28
       * announcing "the widget is built" to a loop that kept asking it to continue. So the nudge
       * says WHERE the JSON failed, in the parser's own words, and asks for only the envelope.
       */
      const lastText = messages[messages.length - 1].content;
      const broken = typeof lastText === 'string' && /"done"\s*:/.test(lastText) ? jsonFault(lastText) : '';
      put(
        'user',
        `Continue. This is step ${run.stepCount + 1}${left ? ` of at most ${run.stepCount + left}` : ''}. ` +
          (broken
            ? `Your last reply looked like a return but could not be read as one JSON object: ${broken}. ` +
              'Reply with ONLY the JSON envelope — nothing before it, nothing after it — and check that every quote, ' +
              'newline and backslash inside a string is escaped and that every brace closes exactly once.'
            : 'If you can answer now, reply with done true and the deliverable. Do not repeat your previous reply.'),
        'continuation',
      );
    }

    const calledAt = Date.now();
    // Accumulated here rather than handed the raw delta, so the caller always receives the WHOLE
    // answer so far — which is what a viewer paints, and what makes a dropped or throttled-away
    // callback cost nothing.
    let streamed = '';
    let thought = '';
    const liveTools: LiveStep['tools'] = [];
    const liveParts: LivePart[] = [];
    const tailPart = (): LivePart | undefined => liveParts[liveParts.length - 1];
    const live = (): LiveStep => ({ text: streamed, reasoning: thought, tools: liveTools, parts: liveParts });
    const reply = await chat(messages, {
      model: run.model || defaultModel,
      effort: run.effort || '',
      tools: useTools && !ctx.lastCall ? tools!(run) : undefined,
      systemPrompts: system,
      // The person's Stop, so it ends the call in flight rather than the one after it.
      ...(ctx.signal ? { signal: ctx.signal } : {}),
      ...(onPartial
        ? {
            onReasoning: (d: string) => {
              thought += d;
              const l = tailPart();
              if (l?.kind === 'reasoning') l.text += d;
              else liveParts.push({ kind: 'reasoning', text: d });
              onPartial(live(), run, ctx);
            },
            // A tool the moment it is NAMED, and its arguments as they arrive — so a statechart
            // being written through `models.write` is watchable in an agent's thread exactly as
            // it is in a person's own.
            onToolCall: (c: { id: string; name: string }) => {
              if (!liveTools.some((x) => x.id === c.id)) {
                liveTools.push({ ...c, argumentsJson: '' });
                liveParts.push({ kind: 'tool', id: c.id });
              }
              onPartial(live(), run, ctx);
            },
            onToolArgs: (id: string, delta: string) => {
              const hit = liveTools.find((x) => x.id === id);
              if (hit) hit.argumentsJson += delta;
              onPartial(live(), run, ctx);
            },
            onDelta: (d: string) => {
              streamed += d;
              const l = tailPart();
              if (l?.kind === 'text') l.text += d;
              else liveParts.push({ kind: 'text', text: d });
              // The run AND the context: publishing this means writing to the store, and a write to
              // a run tree presents the epoch of the lease its driver holds. Reading that off the
              // step's own context is what keeps the publish from being fenced by the very lease
              // the step is running under.
              onPartial(live(), run, ctx);
            },
          }
        : {}),
    });
    const inferenceMs = Date.now() - calledAt;

    /*
     * A STOP THAT LANDED WHILE THE MODEL WAS ANSWERING ENDS THE STEP HERE — before anything is
     * written or run.
     *
     * The model names tools in the reply the person was waiting on when they pressed Stop, and a
     * write to a machine cannot be taken back. Observed live: `widgets.check` ran 12.9s after a
     * stop. Checked before the reply's prose is written as well: a reply that landed after the stop
     * put "Waiting for the widget…" into a thread whose agent had been stopped 3.6s earlier. The
     * round trip to ask again is paid only when the reply names tools — a reply that is only prose
     * is refused by the store at the progress write that follows.
     */
    if (ctx.signal?.aborted || (useTools && reply.toolCalls?.length && ctx.stillLive && !(await ctx.stillLive()))) {
      return {
        headline: 'stopped',
        usage: reply.usage && {
          inputTokens: BigInt(reply.usage.inputTokens),
          outputTokens: BigInt(reply.usage.outputTokens),
          costUsd: reply.usage.costUsd,
        },
      } as StepResult;
    }

    // A message with no content is not a message. Appending it would put a turn in the durable log
    // that says nothing, and, because the next step reads that log, would change the request on
    // the retry in a way that makes "the model produced nothing" harder to recognise rather than
    // easier. Skipping it keeps the retry a true retry.
    const produced = reply.text.trim().length > 0;
    if (produced) await io.append(run.conversationId, 'assistant', reply.text, reply.reasoning, reply.reasoningMs);

    const telemetry: StepTelemetry = {
      elapsedMs: Date.now() - startedAt,
      inferenceMs,
      ttftMs: reply.ttftMs, // absent stays absent
      context: { chars, tokens: attribute(chars, reply.usage), messageCount: messages.length },
    };

    const parsed = parseReply(reply.text);
    const usage = reply.usage && {
      inputTokens: BigInt(reply.usage.inputTokens),
      outputTokens: BigInt(reply.usage.outputTokens),
      costUsd: reply.usage.costUsd,
    };

    // A TURN THAT CALLED A TOOL HAS NOT FINISHED THINKING.
    //
    // It is progress, not a conclusion: the model asked for something and must be called again with
    // the answer. Handled before the empty-output check because a tool call IS output: a model that
    // called a tool and wrote no prose did work, and treating that as "produced nothing" would end a
    // run at its most productive moment.
    if (useTools && reply.toolCalls?.length) {
      // PHASE 1, recorded before anything runs. See `ConversationIO.appendToolCalls`.
      // The thinking that led to these calls rides this row when there was no prose to carry it —
      // otherwise it is on the prose row above and would be recorded twice.
      await io.appendToolCalls!(run.conversationId, reply.toolCalls, produced ? undefined : reply.reasoning, produced ? undefined : reply.reasoningMs);
      // Which tools refused this step, and which answered. Sets rather than counts: six parallel
      // searches where one fails is a failing tool once, not one sixth of one.
      const failed = new Set<string>();
      const worked = new Set<string>();
      /** What each refusing tool actually SAID, by name — the reason the breaker quotes when it
       *  stops a run, because the parent receives a sentence and never the child's transcript. */
      const failures = new Map<string, string>();
      for (const call of reply.toolCalls) {
        // A stop between two calls of one batch: the rest are answered as not run, so each call
        // recorded above still has its result and none reads as an outcome nobody knows.
        if (ctx.signal?.aborted) {
          await io.appendToolResult!(run.conversationId, call.id, { ok: false, message: `"${call.name}" was not run: this agent was stopped.` });
          continue;
        }
        // Never throws by contract, but a dispatcher that did would strand a phase-1 record with no
        // phase 2, which the next step correctly reads as UNKNOWN. Catching here keeps that the
        // dispatcher's meaning rather than an accident of this loop.
        const outcome = await dispatch!(call, { runId: run.runId, epoch: ctx.epoch, conversationId: run.conversationId }).catch((e: unknown): ToolOutcome => ({
          ok: false,
          message: `"${call.name}" could not be run: ${e instanceof Error ? e.message : String(e)}`,
        }));
        // PHASE 2, resolves the record above — unless the call is answered later (see `deferred`),
        // in which case the open call IS the record, and resolving it now would end the wait before
        // it began.
        if (!outcome.deferred) await io.appendToolResult!(run.conversationId, call.id, outcome);
        /*
         * WHETHER IT WORKED, kept for the loop — which otherwise cannot see it.
         *
         * A tool turn reports only a headline, so a run could call one refusing tool until its cap
         * and every step looked like progress. The judgement belongs here for the same reason
         * `unproductive` does: the step holds the outcome, the loop holds only a sentence about it.
         *
         * BY NAME, never by name-and-arguments. A model that cannot get a tool to work rewrites its
         * arguments on every attempt, so a key that included them would be new each time and would
         * never count to anything.
         *
         * A DEFERRED call is neither: it has no outcome yet, and whatever answers it later writes
         * the result into that same open call. Counting it as failure would end runs that are
         * waiting correctly.
         */
        if (!outcome.deferred) {
          (outcome.ok ? worked : failed).add(call.name);
          /*
           * AND WHAT IT SAID, because "the refusal is in the transcript" turned out not to be true
           * for the one reader who needs it.
           *
           * The breaker ends a run with "X failed 3 times in a row" and pointed the parent at the
           * child's own transcript for the reason. A parent cannot read that: it gets the child's
           * RETURN, and searching the sub-agent's notes for the refusal text surfaced nothing.
           * Observed — a parent told the person the specialist lacked RBAC write permission and
           * walked them into editing roles, when the tool had actually refused because a validator
           * regex was wrong. A wrong cause, stated confidently, with a security change attached.
           *
           * Last one wins: it is the refusal the model failed to act on, and the earlier attempts
           * are the same tool saying the same thing.
           */
          if (!outcome.ok) failures.set(call.name, outcome.message);
        }
      }
      // Each tool ONCE, with a count: six parallel searches read as "using symbols.search ×6",
      // not as the same name six times cut off by the headline's length.
      const counts = new Map<string, number>();
      for (const c of reply.toolCalls) counts.set(c.name, (counts.get(c.name) ?? 0) + 1);
      const names = [...counts].map(([name, n]) => (n > 1 ? `${name} ×${n}` : name)).join(', ');
      return {
        headline: clampHeadline(`using ${names}`),
        usage,
        telemetry,
        ...(failed.size ? { toolsFailed: [...failed] } : {}),
        ...(worked.size ? { toolsWorked: [...worked] } : {}),
        ...(failures.size ? { toolFailureMessages: Object.fromEntries(failures) } : {}),
      } as StepResult;
    }

    // NOTHING AT ALL is a different condition from UNPARSEABLE, and only one of them is worth
    // retrying. A model that rambled said something, and a format reminder can work, so that stays
    // progress. A model that returned empty content produced no information, and the loop would
    // resend an identical request; it says so instead, and the loop ends the run after a second.
    //
    // Observed on reasoning models that spend their whole token budget reasoning and emit empty
    // content with `finish_reason: length`.
    if (!produced) {
      return { headline: 'no output', usage, telemetry, unproductive: true } as StepResult;
    }
    if (!parsed) {
      return { headline: clampHeadline(reply.text.split('\n')[0] || 'thinking'), usage, telemetry } as StepResult;
    }
    const headline = clampHeadline(String(parsed.headline ?? 'working'));
    if (!parsed.done) return { headline, usage, telemetry } as StepResult;

    /*
     * THE CONTRACT IS CHECKED BEFORE THE RUN IS ALLOWED TO CONCLUDE.
     *
     * "The model said done" is not the same as "the answer is usable". Where a caller declared an
     * output schema — a state machine branching on the fields — a conclusion that does not match it
     * is worse than no conclusion, because the caller acts on it. The correction rides back as
     * ordinary progress, so the model gets another try inside the SAME step budget rather than the
     * run ending on output nobody could read. Running past the cap costs one call; concluding on a
     * broken deliverable ships a fabrication as a finding.
     */
    const deliverableJson = parsed.deliverable === undefined ? '' : JSON.stringify(parsed.deliverable);
    if (validateDeliverable) {
      const checked = validateDeliverable(deliverableJson, reply.text);
      if (!checked.ok) {
        await io.append(run.conversationId, 'user', `SYSTEM: that answer was rejected — ${checked.remedy}`);
        return { headline: clampHeadline(checked.remedy), usage, telemetry } as StepResult;
      }
    }

    /*
     * WHERE DID THAT NUMBER COME FROM — asked once, then recorded either way.
     *
     * The evidence is everything this run actually received: its task, every tool result, and every
     * message handed to it (a child's return, a peer's note, the user's own words). A figure in the
     * answer that appears in none of them has no source in the record.
     *
     * ASKED ONCE, NOT ENFORCED, and the difference is the whole design. Whether a number was
     * measured, computed or invented is not decidable from the text — an honest total derived from
     * two readings is unsourced by exactly the same test as a fabrication. So the model gets one
     * chance to point at where it came from or drop it, which is something an honest agent can
     * always do and an inventing one cannot; and if the figure stands, the run still concludes and
     * the finding travels WITH the return, visible to every reader above it.
     *
     * Blocking instead would refuse legitimate arithmetic forever and teach the model to omit
     * figures rather than source them — which is the same information loss with a worse cause.
     *
     * The retry is once per run, tracked on the closure rather than per step: an agent that has
     * already been asked and answered should not be asked again on the next attempt, or a run whose
     * whole job is arithmetic pays the toll on every conclusion.
     */
    const summaryText = String(parsed.summary ?? headline);
    const unsourced = unsourcedValues(deliverableJson, summaryText, evidenceFor(history, run));
    /*
     * ASKED ONCE PER RUN — read off the RUN'S OWN LOG, not off a variable.
     *
     * The obvious place for "have I already asked" is a closure, and it was wrong twice over. This
     * factory is invoked inside the per-step function on the browser path, so the closure is rebuilt
     * on every step and the flag was always false: the agent was challenged, explained itself, was
     * challenged again, and burned its whole step cap without ever concluding. Observed live —
     * ninety seconds stuck on "checking where those figures came from".
     *
     * Even with a stable closure it would be wrong, because a run outlives the thing driving it. A
     * tab that closes and a server that adopts the tree are a NEW driver with new memory, and the
     * agent would be re-challenged on work it had already justified.
     *
     * The log is the one thing that is per-run and durable, and the question is answerable from it
     * exactly: did we already put this to this run?
     */
    /*
     * AND: A REAL READING REPORTED AGAINST THE WRONG THING.
     *
     * Checked BEFORE the unsourced case, because it is the stronger statement and the two are
     * mutually exclusive by construction: a swapped value came from somewhere, so it is never
     * unsourced. Told first, the model fixes a substitution rather than defending a figure it can
     * legitimately account for.
     */
    const calls = recordedCalls(history);
    const wrong = misattributed(deliverableJson, summaryText, calls);
    const alreadyAsked = history.some((m) => m.content.startsWith(ASKED_FOR_SOURCES));

    /*
     * A SOURCE THAT WAS NEVER RETURNED — checked first of the three, because it is the flattest.
     *
     * A cited link either came back from a search or it did not; there is no honest reading of a URL
     * the agent never received. It is also the fabrication a reader is least equipped to catch: a
     * link looks checkable, so it does not get checked.
     */
    const invented = uncitedSources(deliverableJson, summaryText, calls);
    if (invented.length && !alreadyAsked) {
      await io.append(run.conversationId, 'user', `${ASKED_FOR_SOURCES}${uncitedRemedy(invented)} ${RESEND_WHOLE_ANSWER}`);
      return { headline: clampHeadline('checking those sources'), usage, telemetry } as StepResult;
    }
    if (wrong.length && !alreadyAsked) {
      await io.append(run.conversationId, 'user', `${ASKED_FOR_SOURCES}${misattributedRemedy(wrong)} ${RESEND_WHOLE_ANSWER}`);
      return { headline: clampHeadline('checking which reading belongs to which'), usage, telemetry } as StepResult;
    }
    if (unsourced.length && !alreadyAsked) {
      const remedy = unsourcedRemedy(unsourced);
      await io.append(run.conversationId, 'user', `${ASKED_FOR_SOURCES}${remedy} ${RESEND_WHOLE_ANSWER}`);
      return { headline: clampHeadline('checking where those figures came from'), usage, telemetry } as StepResult;
    }

    return {
      headline,
      usage,
      telemetry,
      done: {
        terminalReason: 1 /* TERMINAL_REASON_COMPLETED */,
        summary: summaryText,
        deliverableJson,
        ...(unsourced.length ? { unsourced } : {}),
        ...(() => {
          const asked = askedOf(calls);
          return asked.length ? { asked } : {};
        })(),
        // A contradiction with the record travels as its own finding, not folded into the softer
        // one: "this was never measured" and "this belongs to something else" call for different
        // reactions from whoever reads it.
        ...(wrong.length ? { misattributed: wrong.map((m) => `${m.value} reported of ${m.claimedOf}, actually ${m.actuallyOf}`) } : {}),
        ...(invented.length ? { uncited: invented } : {}),
        /*
         * WHERE EACH FIGURE CAME FROM — attached to every answer, not only to suspect ones.
         *
         * This is not a finding, so it is not conditional on something being wrong. It is the
         * provenance of the numbers a machine will act on, and its whole value is that a reader can
         * see the claim and its origin side by side without anyone having to decide which is right.
         *
         * On the ENVELOPE, never in the deliverable: that payload is the expert's declared schema and
         * reaches an SCXML transition as `_event.data`, so wrapping a value to carry its source would
         * change what every `cond` reads.
         */
        ...(() => {
          const cited = citationsFor(deliverableJson, calls);
          return cited.length ? { citations: cited.map((c) => `${c.value} ← ${c.sources.join(' / ')}`) } : {};
        })(),
      },
    } as StepResult;
  };
}
